import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Collapse,
  Drawer,
  FormControl,
  Grid,
  Icon,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItemButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { ChevronLeftRounded, Close, QrCode } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { encryptURL, decryptURL, findOnArray } from 'utils/functions';
import { GridContext } from 'contexts/GridContext';
import { DropsContext } from 'contexts/DropsContext';
import { RelatoriosContext } from 'contexts/RelatoriosContext';
import { ExpedicoesContext } from 'contexts/ExpedicoesContext';
import { useModal } from 'components/Modals';
import DeleteModal from 'components/Modals/DeleteModal';
import Container from 'components/Container';
import Button from 'components/Button';
import Input from 'components/Input';
import Card from 'components/Card';
import localeText from 'utils/localeText';
import styles from './styles';

const Tab0 = ({ bip1, setBip1, isModalOpen, produtoRef }) => {
  useEffect(() => {
    produtoRef.current.focus();
  }, []);

  const handleBlur = () => !isModalOpen && produtoRef.current.focus();

  const handleChange = (e) => setBip1(e?.target?.value);

  return (
    <Grid item xs={12} display="flex" alignItems="center" gap={5}>
      <QrCode sx={styles?.qrcode} color="primary" />
      <Typography fontWeight="bold" textAlign="start" flex={1}>
        Faça a leitura do produto
      </Typography>
      <input
        ref={produtoRef}
        value={bip1}
        onBlur={handleBlur}
        onChange={handleChange}
        style={styles?.hideInput}
      />
    </Grid>
  );
};

const Tab1 = ({
  bip1,
  bip2,
  setBip2,
  handleSubmit,
  loading,
  quantidadeRef,
}) => {
  const { drops } = useContext(DropsContext);
  const etqProduto = JSON.parse(bip1);
  const produto = findOnArray(etqProduto?.produto_id, drops?.Produto);

  useEffect(() => {
    quantidadeRef.current.focus();
  }, []);

  return (
    <Grid item xs={12} display="flex" alignItems="center" gap={5}>
      <QrCode sx={styles?.qrcode} color="primary" />
      <Box
        flex={1}
        gap={2}
        display="flex"
        flexDirection="column"
        textAlign="start"
      >
        <Typography variant="body1" fontWeight="bold" flex={1}>
          {produto?.label}
        </Typography>
        <Box>
          <Input
            inputRef={quantidadeRef}
            value={bip2}
            onChange={(event) => setBip2(event?.target?.value)}
            label="Quantidade"
            type="number"
            fullWidth={false}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={styles?.btn}
          loading={loading}
        >
          Confirmar
        </Button>
      </Box>
    </Grid>
  );
};

const EmbalagemDrawer = ({ open, onClose, embalagemRef, onSubmit }) => {
  const [value, setValue] = useState('');
  const { drops } = useContext(DropsContext);
  const tipo_id = drops?.LinhaProduto?.find(
    (f) => f?.label === 'EMBALAGEM'
  )?.value;
  const tipos = drops?.Produto?.filter((f) =>
    Boolean(tipo_id) ? f?.celinha_id === tipo_id : false
  );

  useEffect(() => {
    if (open && embalagemRef.current) {
      embalagemRef.current.focus();
    }
  }, [open, embalagemRef]);

  useEffect(() => {
    if (value === '') return;

    const delayDebounce = setTimeout(() => {
      onSubmit(JSON.parse(value));
      setValue('');
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [value]);

  const handleBlur = () => open && embalagemRef.current.focus();

  const handleChange = (e) => setValue(e?.target?.value);

  return (
    <Drawer
      open={open}
      variant="temporary"
      anchor="right"
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{ sx: styles?.drawer }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p="1em"
      >
        <Box flex={1}>
          <Typography variant="h5">Nova Embalagem</Typography>
          <Typography variant="caption">
            Faça a leitura do tipo da embalagem
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Fechar" onClick={onClose}>
          <Close />
        </IconButton>
      </Box>
      <Box sx={styles?.drawerContainer}>
        <Grid container spacing={10}>
          {tipos?.map((t, i) => (
            <Grid key={i?.toString()} item xs={12} textAlign="center">
              <QRCodeSVG value={JSON.stringify({ produto_id: t?.value })} />
              <Typography fontWeight="bold">{t?.label}</Typography>
            </Grid>
          ))}
        </Grid>
      </Box>
      <input
        ref={embalagemRef}
        value={value}
        onBlur={handleBlur}
        onChange={handleChange}
        style={styles?.hideInput}
      />
    </Drawer>
  );
};

const Embalagem = () => {
  const navigate = useNavigate();
  const { hash } = useParams();
  const lotes = decryptURL(hash);
  const produtoRef = useRef(null);
  const embalagemRef = useRef(null);
  const quantidadeRef = useRef(null);
  const refs = useRef({});
  const refx = useRef({});
  const [tab, setTab] = useState(0);
  const [bip1, setBip1] = useState('');
  const [bip2, setBip2] = useState('1');
  const [pedidos, setPedidos] = useState([]);
  const [pedido, setPedido] = useState(null);
  const [embalagens, setEmbalagens] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itensEmbalagens, setItensEmbalagens] = useState([]);
  const [selectedEmbalagem, setSelectedEmbalagem] = useState(null);
  const { openModal, closeModal } = useModal();
  const { postGrid, deleteGrid, deleteLoading } = useContext(GridContext);
  const {
    getEmbalagem,
    embalagemLoading,
    getEmbalagens,
    getEmbalagemEtiqueta,
    getLoading,
    postEmbalagem,
    postLoading,
  } = useContext(ExpedicoesContext);
  const _itensEmbalagens = itensEmbalagens?.filter((f) =>
    Boolean(pedido) ? f?.id === pedido : true
  );
  const _embalagens = embalagens?.filter((f) => {
    if (!Boolean(f?.Itens?.length)) {
      return true;
    } else {
      if (Boolean(pedido)) {
        return f?.Itens?.some((s) => s?.pedido_id == pedido);
      }
      if (Boolean(lotes?.length)) {
        return f?.Itens?.some((s) =>
          lotes?.some((ss) => s?.lote_id === ss?.id)
        );
      }
    }
    return true;
  });

  const renderItens = ({ data = [], item_id = null }) => {
    const agrupado = data?.reduce((acc, item) => {
      const existingPedido = acc.find((grupo) => grupo.id === item.pedido_id);
      if (existingPedido) {
        existingPedido.itens.push(item);
      } else {
        acc.push({
          id: item?.pedido_id,
          pedido: item?.pedido,
          cliente: item?.cliente,
          destino: item?.destino,
          itens: [item],
        });
      }
      return acc;
    }, []);
    const arr = [...new Set(data?.map((item) => item?.pedido_id))];
    setPedidos([
      { value: null, label: 'Todos' },
      ...arr?.map((p) => {
        const pd = data?.find((item) => item?.pedido_id === p);
        return { value: p, label: pd?.pedido, Marketplace: pd?.marketplace };
      }),
    ]);
    if (agrupado?.length > 0) {
      refs.current = agrupado?.reduce((acc, _, index) => {
        acc[index] = React.createRef();
        return acc;
      }, {});
    }
    setItensEmbalagens(agrupado);
    setTab(0);
    setBip1('');
    setBip2('1');
    if (!agrupado?.some((s) => s?.id === pedido)) {
      setPedido(null);
      setSelectedItem(null);
    }
    if (Boolean(item_id)) {
      const index = agrupado
        ?.filter((f) => (Boolean(pedido) ? f?.id === pedido : true))
        ?.findIndex((item) => item?.itens?.some((s) => s?.id === item_id));
      if (index !== -1 && refs.current[index]) {
        setTimeout(() => {
          setSelectedItem(item_id);
          if (Boolean(refs.current[index].current)) {
            refs.current[index].current.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 500);
      }
    }
  };

  const renderEmbalagens = ({ data = [], embalagem_id = null }) => {
    setEmbalagens(data);

    if (data?.length > 0) {
      refx.current = data?.reduce((acc, _, index) => {
        acc[index] = React.createRef();
        return acc;
      }, {});
    }

    if (Boolean(embalagem_id)) {
      const index = data?.findIndex((item) => item?.id === embalagem_id);

      if (index !== -1 && refx.current[index]) {
        setTimeout(() => {
          setSelectedEmbalagem(data[index]);
          if (Boolean(refx.current[index].current)) {
            refx.current[index].current.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 500);
      }
    }
  };

  const loadData = (props) => {
    const embalagem_id = props?.embalagem_id || null;
    const item_id = props?.item_id || null;
    const params = { lotes: lotes?.map((l) => l?.id) };
    getEmbalagem({ params, cb: (data) => renderItens({ data, item_id }) });
    getEmbalagens({ cb: (data) => renderEmbalagens({ data, embalagem_id }) });
    closeModal();
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (!Boolean(tab)) {
      loadData();
    }
  }, [tab]);

  useEffect(() => {
    if (!bip1) return;

    const timeout = setTimeout(() => {
      setTab(1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [bip1]);

  useEffect(() => {
    if (Boolean(_embalagens?.length) && !Boolean(selectedEmbalagem)) {
      setSelectedEmbalagem(_embalagens[0] || null);
    }
  }, [embalagens]);

  useEffect(() => {
    if (!_embalagens?.some((s) => s?.id === selectedEmbalagem?.id)) {
      setSelectedEmbalagem(_embalagens[0]);
    }
  }, [pedido]);

  const onSubmitItem = () => {
    const data = {
      embalagem_id: selectedEmbalagem?.id,
      produto: bip1,
      quantidade: Number(bip2 || 0),
      pedido_id: pedido,
      lotes: lotes?.map((l) => l?.id),
    };
    postEmbalagem({ data, cb: (res) => loadData({ item_id: res?.id }) });
  };

  const onDeleteItem = (id) =>
    openModal(
      <DeleteModal
        onSubmit={() =>
          deleteGrid({
            params: { id, rotina: 'DocumentoItemEmbalagem' },
            cb: () => loadData(),
          })
        }
      />
    );

  const onSubmitEmbalagem = (data = {}) =>
    postGrid({
      data: { data, rotina: 'Embalagem' },
      cb: (res) => loadData({ embalagem_id: res?.id }),
    });

  const onDeleteEmbalagem = (id) =>
    openModal(
      <DeleteModal
        onSubmit={() =>
          deleteGrid({
            params: { id, rotina: 'Embalagem' },
            cb: () => loadData({ embalagem_id: _embalagens?.[0]?.id || null }),
          })
        }
      />
    );

  const onPrintEtiqueta = (id) => getEmbalagemEtiqueta({ params: { id } });
  // getURLRelatorio({
  //   data: {
  //     codigo: 'ETQEMB',
  //     ids: [id],
  //     impressora_id: 2,
  //   },
  // });

  const RenderTabs = () => {
    const props = {
      bip1,
      bip2,
      setBip1,
      setBip2,
      produtoRef,
      isModalOpen,
      quantidadeRef,
      loading: postLoading,
      handleSubmit: onSubmitItem,
    };
    switch (tab) {
      case 0:
        return <Tab0 {...props} />;
      case 1:
        return <Tab1 {...props} />;
      default:
        return null;
    }
  };

  return (
    <Container>
      <Box sx={styles?.headerContainer}>
        <IconButton
          size="small"
          color="primary"
          onClick={() => (Boolean(tab) ? setTab(0) : navigate(-1))}
          sx={styles?.backBtn}
        >
          <ChevronLeftRounded />
        </IconButton>
        <Typography variant="h5" sx={styles?.title}>
          Embalagem
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4} display="flex">
          <Card style={styles?.card} title="Embalagens">
            <Grid container spacing={2}>
              <Grid
                item
                gap={2}
                xs={12}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => onSubmitEmbalagem()}
                >
                  Nova Embalagem
                </Button>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setIsModalOpen(true)}
                >
                  <QrCode />
                </IconButton>
              </Grid>
              <Grid item xs={12} sx={styles?.scroll}>
                {Boolean(_embalagens?.length) ? (
                  <>
                    {getLoading && <LinearProgress />}
                    <List>
                      {(_embalagens || [])?.map((e, i) => (
                        <Box key={`${e?.id}`} ref={refx.current[i]}>
                          <ListItemButton
                            disabled={getLoading}
                            divider={i + 1 < _embalagens?.length}
                            selected={selectedEmbalagem?.id === e?.id}
                            onClick={() => setSelectedEmbalagem(e)}
                          >
                            <Box flex={1}>
                              <Typography fontWeight="bold">
                                EMBALAGEM {e?.id}
                              </Typography>
                              {Boolean(e?.tipo) && (
                                <Typography variant="caption">
                                  {e?.tipo}
                                </Typography>
                              )}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => onPrintEtiqueta(e?.id)}
                            >
                              <Icon>print</Icon>
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                navigate(
                                  `/app/Expedicoes/Embalagem/Edit/${encryptURL(
                                    e
                                  )}`
                                )
                              }
                            >
                              <Icon>edit</Icon>
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => onDeleteEmbalagem(e?.id)}
                            >
                              <Icon>delete</Icon>
                            </IconButton>
                          </ListItemButton>
                          <Collapse
                            in={
                              Boolean(e?.Itens?.length) &&
                              selectedEmbalagem?.id === e?.id
                            }
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box p={2} sx={styles?.backgroundEmb}>
                              <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                textAlign="start"
                              >
                                Itens
                              </Typography>
                              {e?.Itens?.map((item, i) => (
                                <Box
                                  key={i?.toString()}
                                  display="flex"
                                  flex={1}
                                  alignItems="center"
                                >
                                  <Typography
                                    variant="caption"
                                    textAlign="start"
                                  >
                                    {item?.referencia} - {item?.descricao}
                                  </Typography>
                                  <Box
                                    flex={1}
                                    mx={1}
                                    borderBottom="2px dotted black"
                                  />
                                  <Typography
                                    variant="caption"
                                    textAlign="start"
                                    mr={1}
                                  >
                                    {item?.quantidade || 0}{' '}
                                    {item?.unidade_medida || ''}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => onDeleteItem(item?.id)}
                                    disabled={deleteLoading}
                                  >
                                    <Icon sx={styles?.deleteIcon}>
                                      delete_forever
                                    </Icon>
                                  </IconButton>
                                </Box>
                              ))}
                            </Box>
                          </Collapse>
                        </Box>
                      ))}
                    </List>
                  </>
                ) : (
                  <Typography variant="caption">
                    Nenhuma embalagem encontrada
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item xs={12} sm={8} display="flex">
          <Card style={styles?.card} title="Itens">
            <Grid container spacing={2}>
              <RenderTabs />
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <FormControl size="small" sx={styles?.formControl}>
                  <InputLabel size="small" id="pedido_id">
                    Pedido
                  </InputLabel>
                  <Select
                    labelId="pedido_id"
                    size="small"
                    value={pedido}
                    label="Pedido"
                    onChange={(e) => setPedido(e?.target?.value)}
                  >
                    {pedidos?.map((p) => (
                      <MenuItem value={p?.value}>
                        {Boolean(p?.value) && `Pedido Nº `}
                        {p?.label || '-'}
                        {p?.Marketplace && ` (${p?.Marketplace})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sx={styles?.scroll}>
                {Boolean(_itensEmbalagens?.length) ? (
                  _itensEmbalagens?.map((item, idx) => (
                    <Box ref={refs.current[idx]} key={idx?.toString()} mt={2}>
                      <Box display="flex" alignItems="center">
                        <Box flex={1}>
                          <Typography variant="h6" textAlign="start">
                            Pedido {item?.pedido || ''}
                          </Typography>
                          {Boolean(item?.cliente) && (
                            <Typography fontSize={14} textAlign="start">
                              {item?.cliente}
                            </Typography>
                          )}
                          {Boolean(item?.destino) && (
                            <Typography fontSize={14} textAlign="start">
                              {item?.destino}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<Icon>print</Icon>}
                          onClick={() =>
                            navigate(
                              `/app/Expedicoes/Embalagem/Etiqueta/${encryptURL(
                                item?.itens
                              )}`
                            )
                          }
                        >
                          Imprimir Etiquetas
                        </Button>
                      </Box>
                      <DataGrid
                        rows={item?.itens}
                        columns={[
                          {
                            field: 'referencia',
                            headerName: 'Referência',
                            width: 150,
                            sortable: false,
                          },
                          {
                            field: 'descricao',
                            headerName: 'Descrição',
                            flex: 1,
                            sortable: false,
                          },
                          {
                            field: 'quantidade',
                            headerName: 'Quantidade',
                            width: 200,
                            type: 'number',
                            sortable: false,
                            valueGetter: ({ row }) =>
                              `${row?.quantidade_embalada || 0} / ${
                                row?.quantidade || 0
                              } ${row?.um || ''}`,
                          },
                          {
                            field: 'progresso',
                            headerName: 'Progresso',
                            width: 200,
                            type: 'number',
                            sortable: false,
                            renderCell: ({ row }) => {
                              const progress = Math.round(
                                ((row?.quantidade_embalada || 0) /
                                  (row?.quantidade || 0)) *
                                  100
                              );
                              return (
                                <Box
                                  flex={1}
                                  display="flex"
                                  alignItems="center"
                                >
                                  <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={styles?.linear}
                                    color={
                                      progress < 100 ? 'warning' : 'success'
                                    }
                                  />
                                  <Typography variant="body2">
                                    {progress}%
                                  </Typography>
                                </Box>
                              );
                            },
                          },
                        ]}
                        hideFooter
                        autoHeight
                        density="compact"
                        loading={postLoading || embalagemLoading}
                        selectionModel={[selectedItem]}
                        disableColumnMenu
                        showCellRightBorder
                        showColumnRightBorder
                        localeText={localeText}
                        components={{
                          LoadingOverlay: LinearProgress,
                        }}
                      />
                    </Box>
                  ))
                ) : embalagemLoading ? (
                  <LinearProgress />
                ) : (
                  <Typography variant="caption">
                    Nenhum item encontrado
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
      <EmbalagemDrawer
        open={isModalOpen}
        embalagemRef={embalagemRef}
        onClose={() => setIsModalOpen(false)}
        onSubmit={onSubmitEmbalagem}
      />
    </Container>
  );
};

export default Embalagem;
